import React, { useLayoutEffect } from "react";
// import { useTranslation } from "react-i18next";
import SplitType from "split-type";
import gsap from "gsap";
import OtherProjects from "../../components/Other-Projects/otherProjects";
import ProjectNav from "../../components/Project-Nav/projectNav";

export default function VCXProject() {
  // const { t } = useTranslation();

  useLayoutEffect(() => {
    const text = new SplitType(".words1", { types: "chars" });
    const textA = SplitType.create(".sentence1", { types: "words" });
    const textB = SplitType.create(".line1", { types: "lines" });

    gsap.fromTo(
      text.chars,
      { y: 0, opacity: 0 },
      { y: -12, opacity: 1, stagger: 0.03 }
    );
    gsap.fromTo(
      textA.words,
      { y: 0, opacity: 0, delay: 0.5 },
      { y: -12, opacity: 1, stagger: 0.06 }
    );
    gsap.fromTo(
      textB.lines,
      { y: 0, opacity: 0, delay: 0.5 },
      { y: -12, opacity: 1, stagger: 0.1 }
    );

    gsap.fromTo(
      ".box1",
      { y: 0, opacity: 0, delay: 1 },
      { y: -20, opacity: 2, duration: 1 }
    );

    gsap.config({
      force3D: false,
      nullTargetWarn: false,
      trialWarn: false,
    });
  });

  return (
    <div className="project-section case-study vcx-project container mt-120">
      <div className="project-hero-card bg-secondary radius-20 w-100 p-40 sm:p-20 xs:p-20 box1">
        <div className="project-hero-title mb-20 sm:mb-12 xs:mb-8">
          <h1 className="f-40 xs:f-18 xs:l-h-24 sm:f-32 l-h-40 f-w-600 text-primary words1">
            VCX — Virtual Card Exchange
          </h1>
          <p className="f-16 xs:f-12 xs:l-h-20 sm:f-14 l-h-30 f-w-300 text-tertiary mt-8 sentence1">
            <span>Neo-banking · Design Sprint Hackathon · Team of 3</span>
          </p>
        </div>
      </div>

      <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary mt-24 mb-20 mw-680 line1">
        A virtual card neo-banking app designed entirely in a 32-hour
        hackathon. Our team of 3 had to research, map the flow, design the
        screens, and build a working prototype before the clock ran out.
        We placed runner-up. The time pressure was intense but this is
        honestly one of the most useful projects I've been part of.
      </p>

      <div className="cs-pills">
        <span className="cs-pill">Neo-banking</span>
        <span className="cs-pill">32-Hour Sprint</span>
        <span className="cs-pill">Runner-up 🥈</span>
        <span className="cs-pill">Team of 3</span>
      </div>

      <div className="cs-stats">
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#a78bfa" }}>32h</div>
          <div className="cs-stat-l">Total sprint window</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#fbbf24" }}>🥈</div>
          <div className="cs-stat-l">Runner-up</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#38bdf8" }}>3</div>
          <div className="cs-stat-l">Team members</div>
        </div>
        <div className="cs-stat">
          <div className="cs-stat-n" style={{ color: "#4ade80" }}>✓</div>
          <div className="cs-stat-l">Full prototype delivered</div>
        </div>
      </div>

      <div className="mb-32">
        <div className="cs-lbl">The product</div>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary line1">
          VCX is a neo-banking platform built around virtual cards — giving
          users real-time visibility and control over their spending. The
          brief was to make everyday banking feel more like a consumer app
          than a financial institution. The constraint: the entire thing
          had to be researched, designed, and prototyped in 32 hours.
        </p>
      </div>

      <div className="mb-32">
        <div className="cs-lbl">My role</div>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary line1">
          Part of a 3-person team. I handled the user flow diagram, card
          management screens, spending analytics views, and the main
          dashboard design. We all collaborated on research and the
          prototype assembly during the sprint.
        </p>
      </div>

      <div className="mb-8">
        <div className="cs-lbl">Research</div>
        <h3 className="f-20 l-h-28 f-w-600 text-primary mb-12 sentence1">
          32 hours meant being strategic about research
        </h3>
        <p className="f-16 xs:f-14 xs:l-h-24 l-h-28 f-w-400 text-tertiary mb-12 line1">
          No time for a full competitive audit. We did a rapid scan of
          Revolut and N26 — borrowed proven patterns for card management
          and spending views — then used internal team members as
          stand-ins for real customers. Short interview session before any
          design started to anchor our decisions in something real rather
          than just assumptions.
        </p>
        <div className="cs-quote">
          <p>
            "I check my bank app every day but it just tells me what
            happened. I want to feel like I'm actually in control, not
            reading a receipt after the fact."
          </p>
          <span>— Proxy user, sprint research session</span>
        </div>
      </div>

      <div className="cs-img bg-white radius-20 minh-600 sm:minh-450 xs:minh-200 mt-32 box1 vcx-userflow"></div>

      <div className="mt-32 mb-8">
        <div className="cs-lbl">Pain points</div>
        <div className="cs-pains">
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              Banking dashboards show balance and transaction history — but
              no proactive insight about spending patterns or upcoming
              issues
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              Virtual card creation took too many steps — something that
              should feel instant required 4-6 screens on existing apps
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag h">Critical</span>
            <div className="cs-pain-text">
              Spending categories were buried in settings — useful
              information hidden where nobody looks
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag m">Medium</span>
            <div className="cs-pain-text">
              Card freeze was too hard to reach — a security-critical
              action that needed to be one tap, not buried in a menu
            </div>
          </div>
          <div className="cs-pain">
            <span className="cs-tag m">Medium</span>
            <div className="cs-pain-text">
              Most banking apps look corporate and cold — no sense of trust
              or confidence for younger, digital-native users
            </div>
          </div>
        </div>
      </div>

      <div className="mt-32 mb-8">
        <div className="cs-lbl">Design decisions</div>
        <div className="cs-two-col">
          <div className="cs-card box1">
            <h4>Flow before frames</h4>
            <p>
              Mapped the complete user flow in FigJam before anyone opened
              a Figma frame. Kept all 3 of us aligned so we could work on
              different screens simultaneously without the product falling
              apart.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>Virtual card as the hero</h4>
            <p>
              Card sits front and centre on the dashboard — not in a menu.
              Purple gradient, prominent numbers. Every primary action
              flows from it. Set the visual direction for the whole product
              in the first hour.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>Spending on the main screen</h4>
            <p>
              Spending breakdown by category is on the primary dashboard,
              not hidden in an analytics section. The biggest difference
              from Revolut and N26's layout — and the thing stakeholders
              noticed first.
            </p>
          </div>
          <div className="cs-card box1">
            <h4>Hand off live, not at the end</h4>
            <p>
              Shared screens with our developer the moment each flow was
              validated. Talking through edge cases live instead of
              writing specs kept the build moving within the 32-hour
              window.
            </p>
          </div>
        </div>
      </div>

      <div className="cs-img bg-secondary radius-20 minh-600 sm:minh-450 xs:minh-200 mt-32 box1 vcx-img1"></div>
      <div className="cs-img bg-secondary radius-20 minh-600 sm:minh-450 xs:minh-200 mt-32 box1 vcx-img2"></div>

      <div className="mt-32 mb-8">
        <div className="cs-lbl">Outcomes</div>
        <div className="cs-outcomes">
          <div className="cs-outcome">
            <div className="ico">🥈</div>
            <h4>Runner-up</h4>
            <p>
              Placed second — recognised for design quality, prototype
              completeness, and clarity of the user flow under pressure.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">⚡</div>
            <h4>Full flow in 32 hours</h4>
            <p>
              Research, flow, wireframes, high-fidelity screens, and
              interactive prototype — all done before the deadline.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">💳</div>
            <h4>Card flow complete</h4>
            <p>
              Virtual card creation, management, freeze/unfreeze, and
              spending analytics — all designed and prototyped.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">🤝</div>
            <h4>Team confidence</h4>
            <p>
              Validated that we could produce competitive design work
              under serious time pressure — changed how I approached every
              sprint after.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">🔄</div>
            <h4>Live handoff model</h4>
            <p>
              Sharing screens as each flow finished — rather than waiting —
              became something I've used in every time-boxed sprint since.
            </p>
          </div>
          <div className="cs-outcome">
            <div className="ico">🧭</div>
            <h4>Flow as shared contract</h4>
            <p>
              The user flow created before design kept the whole team
              aligned with zero alignment meetings during the sprint
              itself.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-32">
        <div className="cs-lbl">Reflection</div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>Constraints actually help.</strong> The 32-hour limit
            forced decisions that weeks of open-ended work might never
            reach. We couldn't debate every component choice — had to
            commit and move. The product came out more decisive for it.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>A user flow is the team's shared language.</strong>{" "}
            Mapping the complete journey before any visual work meant we
            could work in parallel without the product falling apart. The
            flow was the contract between us.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>'Good enough to validate' is a real design standard.</strong>{" "}
            Deciding what needs more time vs. what's good enough to test is
            a skill. This sprint made that instinct explicit — and it's
            stayed useful on every sprint since.
          </p>
        </div>
        <div className="cs-reflection-item">
          <span className="marker">→</span>
          <p>
            <strong>If I did this again:</strong> Even 3-5 real user
            interviews would have improved the spending analytics section.
            Proxy research works for flow logic but misses the emotional
            nuances around money anxiety that only real users bring.
          </p>
        </div>
      </div>

      <div className="project-bottom-nav mt-60 mb-100 flex justify-between align-center box1">
        <div className="flex xs:justify-center">
          <OtherProjects active="vcx" />
        </div>
        <ProjectNav current="vcx" />
      </div>
    </div>
  );
}
